import { motion } from 'framer-motion';
import { Award, Target, Users, TrendingUp } from 'lucide-react';

export const TrustSection = () => {
  const stats = [
    {
      icon: <Users className="w-6 h-6" />,
      value: '50+',
      label: 'Happy Clients',
      color: 'from-[#007AFF] to-[#00F0FF]',
    },
    {
      icon: <Award className="w-6 h-6" />,
      value: '85%',
      label: 'Avg. Conversion Increase',
      color: 'from-[#00F0FF] to-[#007AFF]',
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      value: '3x',
      label: 'ROI for Clients',
      color: 'from-[#007AFF] to-[#00F0FF]',
    },
    {
      icon: <Target className="w-6 h-6" />,
      value: '100%',
      label: 'On-Time Delivery',
      color: 'from-[#00F0FF] to-[#007AFF]',
    },
  ];

  const reasons = [
    {
      title: 'Proven Track Record',
      description: 'Over 50 successful projects delivered with measurable results and happy clients.',
    },
    {
      title: 'Modern Technology',
      description: 'We use the latest web technologies to build fast, secure, and scalable websites.',
    },
    {
      title: 'Transparent Process',
      description: 'Clear communication, regular updates, and collaborative approach throughout.',
    },
    {
      title: 'Post-Launch Support',
      description: '24/7 support, regular maintenance, and optimization to ensure peak performance.',
    },
  ];

  return (
    <section id="trust" className="py-24 md:py-32 relative" data-testid="trust-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#007AFF] mb-4 block" data-testid="trust-label">
            Why Choose Us
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="trust-title">
            Built on{' '}
            <span className="bg-gradient-to-r from-[#007AFF] to-[#00F0FF] bg-clip-text text-transparent">
              Trust & Results
            </span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto leading-relaxed" data-testid="trust-description">
            We don't just build websites — we build digital experiences that drive real business growth.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
              className="glass-card p-6 rounded-2xl text-center cursor-hover"
              data-testid={`stat-card-${index}`}
            >
              <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${stat.color} flex items-center justify-center mx-auto mb-4 text-white`}>
                {stat.icon}
              </div>
              <div className="text-3xl font-black mb-2" data-testid={`stat-value-${index}`}>
                {stat.value}
              </div>
              <div className="text-sm text-zinc-500" data-testid={`stat-label-${index}`}>
                {stat.label}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {reasons.map((reason, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="glass-card p-8 rounded-2xl hover:border-[#007AFF] transition-all"
              data-testid={`reason-card-${index}`}
            >
              <h3 className="text-xl font-semibold mb-3" data-testid={`reason-title-${index}`}>
                {reason.title}
              </h3>
              <p className="text-zinc-400 leading-relaxed" data-testid={`reason-description-${index}`}>
                {reason.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};